import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Play, BarChart3, BookOpen, Zap, Code } from 'lucide-react';
import { Button, ProgressBar, LoadingSpinner } from '../components';
import { useProgress } from '../hooks/useProgress';
import { usePyodide } from '../hooks/usePyodide';
import challengesData from '../data/challenges.json';
import { version } from '../../package.json';

export default function Home() {
  const { progress, isLoaded, xpPerLevel, syncWithChallenges } = useProgress();
  const { isLoading, isReady, loadingStatus } = usePyodide();

  // Sync progress with current challenges (recalculates completions if challenges were added/removed)
  useEffect(() => {
    if (isLoaded && progress) {
      syncWithChallenges(challengesData.challenges);
    }
  }, [isLoaded, syncWithChallenges]);

  // Calculate total challenges count
  const totalChallenges = Object.values(challengesData.challenges).reduce(
    (sum, topicChallenges) => sum + topicChallenges.length, 0
  );

  if (!isLoaded) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner message="Loading progress..." />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header */}
      <header className="p-4 border-b border-[#2a2d31]">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-game-primary to-game-teal-dark flex items-center justify-center">
              <span className="text-white font-bold text-lg">M</span>
            </div>
            <h1 className="text-2xl font-bold text-game-text">
              M-Quest
            </h1>
          </div>
          <a href="https://datameerkat.com" target="_blank" rel="noopener noreferrer" className="text-game-text-dim hover:text-game-text transition-colors text-sm font-medium">
            DataMeerkat Blog
          </a>
        </div>
      </header>

      {/* Main content */}
      <main className="flex-1 flex items-center justify-center p-4">
        <div className="max-w-lg w-full text-center animate-fade-in">
          {/* Logo/Title */}
          <div className="mb-10">
            <div className="w-24 h-24 mx-auto mb-6 rounded-3xl bg-gradient-to-br from-game-primary to-game-teal-dark flex items-center justify-center shadow-2xl shadow-game-primary/30">
              <span className="text-white font-bold text-4xl">M</span>
            </div>
            <h2 className="text-4xl font-extrabold text-game-text mb-3">
              Learn M Language
            </h2>
            <p className="text-game-text-dim text-lg">
              Master Power Query through interactive challenges
            </p>
          </div>

          {/* Python loading status */}
          {isLoading && (
            <div className="mb-6 p-4 rounded-2xl bg-game-surface border border-[#2a2d31]">
              <LoadingSpinner size={20} message={loadingStatus} />
            </div>
          )}

          {/* Player stats */}
          <div className="mb-8 p-6 rounded-2xl bg-game-surface border border-[#2a2d31]">
            <div className="flex items-center justify-between mb-4">
              <span className="text-game-text-dim font-medium">Level {progress?.level || 1}</span>
              <span className="text-game-accent font-bold text-lg">
                {progress?.total_xp || 0} XP
              </span>
            </div>
            <ProgressBar
              value={progress?.current_xp || 0}
              max={xpPerLevel}
              showLabel={false}
              height="h-3"
              color="bg-game-primary"
            />
            <div className="mt-4 flex justify-between text-sm text-game-text-dim">
              <span>{progress?.challenges_completed || 0} / {totalChallenges} challenges completed</span>
              <span>{progress?.achievements?.length || 0} achievements</span>
            </div>
          </div>

          {/* Action buttons */}
          <div className="space-y-4">
            <Link to="/topics" className="block">
              <Button
                size="lg"
                className="w-full"
                disabled={!isReady}
              >
                <Play size={20} className="mr-2" />
                {progress?.challenges_completed > 0 ? 'Continue Learning' : 'Start Learning'}
              </Button>
            </Link>

            <Link to="/playground" className="block">
              <Button variant="secondary" size="lg" className="w-full">
                <Code size={20} className="mr-2" />
                Playground
              </Button>
            </Link>

            <div className="grid grid-cols-2 gap-4">
              <Link to="/progress">
                <Button variant="secondary" className="w-full">
                  <BarChart3 size={18} className="mr-2" />
                  Progress
                </Button>
              </Link>
              <a
                href="https://learn.microsoft.com/en-us/powerquery-m/?wt.mc_id=DP-MVP-5003801"
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button variant="secondary" className="w-full">
                  <BookOpen size={18} className="mr-2" />
                  M Docs
                </Button>
              </a>
            </div>
          </div>

          {/* Features */}
          <div className="mt-14 grid grid-cols-3 gap-6 text-center">
            <div className="p-4 rounded-2xl bg-game-surface border border-[#2a2d31]">
              <Zap className="mx-auto mb-3 text-game-accent" size={28} />
              <p className="text-sm text-game-text-dim font-medium">Interactive Challenges</p>
            </div>
            <div className="p-4 rounded-2xl bg-game-surface border border-[#2a2d31]">
              <BookOpen className="mx-auto mb-3 text-game-primary" size={28} />
              <p className="text-sm text-game-text-dim font-medium">{challengesData.topics.length} Topics</p>
            </div>
            <div className="p-4 rounded-2xl bg-game-surface border border-[#2a2d31]">
              <BarChart3 className="mx-auto mb-3 text-game-primary" size={28} />
              <p className="text-sm text-game-text-dim font-medium">Track Progress</p>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="p-6 text-center text-sm text-game-text-dim border-t border-[#2a2d31]">
        Learn M Language (Power Query) • No account needed • Progress saved locally • <a href="https://datameerkat.com" target="_blank" rel="noopener noreferrer">DataMeerkat</a> • v{version}
      </footer>
    </div>
  );
}
